import * as React from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { IconX } from "@tabler/icons-react";

import { cn } from "@/lib/utils";
import { ClassValue } from "clsx";

const Dialog = DialogPrimitive.Root;

const DialogTrigger = DialogPrimitive.Trigger;

const DialogPortal = DialogPrimitive.Portal;

const DialogClose = DialogPrimitive.Close;

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "fixed inset-0 z-50 bg-black/80  data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className,
    )}
    {...props}
  />
));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

interface DialogContentProps
  extends React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> {
  variant?: "full-screen" | "modal" | "dialog";
  icon?: React.ReactNode;
  iconWrapperClassName?: ClassValue;
  subTitle?: string;
  collapse?: boolean;
  titleClassName?: ClassValue;
  xClassName?: ClassValue;
}

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  DialogContentProps
>(
  (
    {
      className,
      children,
      title,
      subTitle,
      variant = "dialog",
      icon,
      collapse = false,
      iconWrapperClassName,
      titleClassName,
      xClassName,
      ...props
    },
    ref,
  ) => (
    <DialogPortal>
      <DialogOverlay />
      <DialogPrimitive.Content
        ref={ref}
        className={cn(
          "fixed left-[50%] top-[50%] z-50 w-full translate-x-[-50%] translate-y-[-50%] gap-4 border bg-background pb-3xl shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-lg",
          variant === "full-screen" && "w-full h-full",
          variant === "dialog" && "max-w-[400px]",
          variant === "modal" && "w-[480px]",
          className,
        )}
        {...props}
      >
        {icon && (
          <div
            className={cn(
              "size-[48px] rounded-full mb-[16px] justify-center items-center flex mt-2xl ml-2xl",
              iconWrapperClassName,
            )}
          >
            {icon}
          </div>
        )}
        {title && (
          <DialogPrimitive.Title
            className={cn(
              variant === "modal" && "text-xl-bold",
              variant === "dialog" && "text-lg-semibold",
              "flex flex-col",
              titleClassName,
            )}
          >
            <span className="pl-3xl">{title}</span>
            {subTitle && (
              <span className="pl-3xl text-sm-regular">{subTitle}</span>
            )}
            {!collapse && <div className="h-[1px] w-full bg-gray-200 mt-2xl" />}
          </DialogPrimitive.Title>
        )}
        {children}
        <DialogPrimitive.Close className="appearance-none border-none bg-transparent absolute right-xl top-xl opacity-70 transition-opacity hover:opacity-100 focus:outline-none disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground">
          <IconX className={cn("h-6 w-6", xClassName)} />
          <span className="sr-only">Close</span>
        </DialogPrimitive.Close>
      </DialogPrimitive.Content>
    </DialogPortal>
  ),
);
DialogContent.displayName = DialogPrimitive.Content.displayName;

const DialogMain = ({
  className,
  children,
  description,
  ...props
}: React.HTMLAttributes<HTMLDivElement> & { description: string }) => (
  <div className={cn("p-4xl flex flex-col", className)} {...props}>
    <DialogDescription className="sr-only">{description}</DialogDescription>
    {children}
  </div>
);

DialogMain.displayName = "DialogMain";

const DialogHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col space-y-1.5 text-center sm:text-left text-lg-semibold",
      className,
    )}
    {...props}
  />
);
DialogHeader.displayName = "DialogHeader";

const DialogFooter = ({
  className,
  collapse = false,
  lineClassName,
  ...props
}: React.HTMLAttributes<HTMLDivElement> & {
  collapse?: boolean;
  lineClassName?: string;
}) => (
  <div>
    {!collapse && (
      <div className={cn("h-[1px] w-full bg-gray-200 mt-3xl", lineClassName)} />
    )}

    <div
      className={cn(
        "flex flex-col-reverse sm:flex-row sm:justify-end sm:gap-lg sm:items-center px-4xl",
        !collapse && "pt-4xl",
        className,
      )}
      {...props}
    />
  </div>
);
DialogFooter.displayName = "DialogFooter";

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn(
      "text-text-primary leading-none tracking-tight mt-0",
      className,
    )}
    {...props}
  />
));
DialogTitle.displayName = DialogPrimitive.Title.displayName;

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("text-sm-regular text-tertiary mb-4xl", className)}
    {...props}
  />
));
DialogDescription.displayName = DialogPrimitive.Description.displayName;

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogTrigger,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
  DialogMain,
};
